import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';
import HelpSectionHint from '../../components/portal/HelpSectionHint';
import { CARE_ROLE_OPTIONS, resolveKnownCareRoleLabel } from '../../config/careRoles';
import {
  CalendarClock,
  Loader2,
  Plus,
  MoreHorizontal,
  UserPlus,
  UserX,
  CheckCircle2,
  Eye,
  Download,
  Printer,
  AlertTriangle,
  MapPin,
  QrCode,
  Users,
  Receipt,
} from 'lucide-react';

const API = API_BASE;

const STATUS_OPTIONS = ['open', 'assigned', 'completed', 'cancelled'];

const _FMTMONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function formatDateTime(value) {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  const d   = String(dt.getDate()).padStart(2, '0');
  const mo  = _FMTMONTHS[dt.getMonth()];
  const y   = dt.getFullYear();
  const h   = String(dt.getHours()).padStart(2, '0');
  const min = String(dt.getMinutes()).padStart(2, '0');
  return `${d} ${mo} ${y}, ${h}:${min}`;
}

function toIso(value) {
  if (!value) return null;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString();
}

function formatDeviationLabel(deviationMin) {
  if (deviationMin === null || deviationMin === undefined) return null;
  const abs = Math.abs(deviationMin);
  if (abs < 1) return 'on time';
  return deviationMin < 0 ? `${abs}m early` : `${abs}m late`;
}

const ATTENDANCE_LATE_GRACE_MINUTES = 5;

function buildAttendanceWarningFlags(row) {
  if (!row) return [];
  const flags = [];
  if (row.clock_in_early_flag) flags.push('clock_in_early');
  if (row.clock_in_deviation_min != null && row.clock_in_deviation_min > ATTENDANCE_LATE_GRACE_MINUTES) {
    flags.push('clock_in_late');
  }
  if (row.clock_out_deviation_min != null && row.clock_out_deviation_min > ATTENDANCE_LATE_GRACE_MINUTES) {
    flags.push('clock_out_late');
  } else if (row.clock_out_deviation_min != null && row.clock_out_deviation_min < -ATTENDANCE_LATE_GRACE_MINUTES) {
    flags.push('clock_out_early');
  }
  if (row.site_token_mismatch_flag) flags.push('qr_mismatch_clock_in');
  else if (row.site_token_verified === false) flags.push('qr_skipped_clock_in');
  if (row.clock_out_at) {
    if (row.clock_out_site_token_mismatch_flag) flags.push('qr_mismatch_clock_out');
    else if (row.clock_out_site_token_verified === false) flags.push('qr_skipped_clock_out');
  }
  if (row.clock_in_geo_flag === 'location_denied') flags.push('gps_denied_clock_in');
  else if (!row.clock_in_latitude) flags.push('gps_missing_clock_in');
  if (row.clock_out_geo_flag === 'location_denied') flags.push('gps_denied_clock_out');
  else if (row.clock_out_at && !row.clock_out_latitude) flags.push('gps_missing_clock_out');
  return flags;
}

function formatApprovalTierLabel(tier) {
  if (tier === 'warning') return 'Warning';
  if (tier === 'corrected') return 'Corrected';
  return 'Clean';
}

function isoToDatetimeLocalValue(iso) {
  if (!iso) return '';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function getPayableClockIn(row) {
  return row?.payable_clock_in_at || row?.corrected_clock_in_at || row?.clock_in_at;
}

function getPayableClockOut(row) {
  return row?.payable_clock_out_at || row?.corrected_clock_out_at || row?.clock_out_at;
}

function formatEvidenceSourceLabel(source) {
  if (!source) return '—';
  return source.replace(/_/g, ' ');
}

const CORRECTION_EVIDENCE_SOURCE_OPTIONS = [
  { value: 'client_confirmed', label: 'Client confirmed' },
  { value: 'phone_call', label: 'Phone call' },
  { value: 'rota_correction', label: 'Rota correction' },
  { value: 'manager_decision', label: 'Manager decision' },
  { value: 'other', label: 'Other' },
];

function formatWarningFlagsForDisplay(flags) {
  if (!flags?.length) return '—';
  return flags.join('; ');
}

const ATTENDANCE_ON_HOLD_RESOLUTIONS = new Set(['admin_correction_required', 'escalated']);

function isAttendanceOnHoldRecord(row) {
  return ATTENDANCE_ON_HOLD_RESOLUTIONS.has(row?.rejection_resolution);
}

function getAttendanceOnHoldMeta(resolution) {
  if (resolution === 'admin_correction_required') {
    return {
      badgeLabel: 'Admin correction required',
      statusLine: 'Admin correction required — worker cannot resubmit',
      badgeClass: 'bg-orange-50 text-orange-800 border-orange-200',
    };
  }
  if (resolution === 'escalated') {
    return {
      badgeLabel: 'Escalated',
      statusLine: 'Escalated / under investigation — worker cannot resubmit',
      badgeClass: 'bg-purple-50 text-purple-800 border-purple-200',
    };
  }
  return {
    badgeLabel: 'On hold',
    statusLine: 'Attendance on hold',
    badgeClass: 'bg-slate-50 text-slate-700 border-slate-200',
  };
}

function renderSiteQrEvidence(prefix, row) {
  const verifiedKey = prefix === 'out' ? 'clock_out_site_token_verified' : 'site_token_verified';
  const mismatchKey = prefix === 'out' ? 'clock_out_site_token_mismatch_flag' : 'site_token_mismatch_flag';
  const methodKey = prefix === 'out' ? 'clock_out_site_token_method' : 'site_token_method';
  const label = prefix === 'out' ? 'Clock-out QR' : 'Clock-in QR';
  if (row[verifiedKey] === true) {
    return (
      <p className="text-xs flex items-center gap-1 text-green-700">
        <QrCode className="h-3 w-3 shrink-0" />
        {label}: verified ({row[methodKey] === 'manual_code' ? 'manual code' : 'camera scan'})
      </p>
    );
  }
  if (row[mismatchKey]) {
    return (
      <p className="text-xs flex items-center gap-1 text-red-700 font-medium">
        <AlertTriangle className="h-3 w-3 shrink-0" />
        {label}: token mismatch — review carefully
      </p>
    );
  }
  if (row[verifiedKey] === false) {
    return (
      <p className="text-xs flex items-center gap-1 text-amber-700">
        <AlertTriangle className="h-3 w-3 shrink-0" />
        {label}: skipped (GPS only)
      </p>
    );
  }
  return null;
}

function renderGpsEvidence(prefix, row) {
  const latKey = prefix === 'out' ? 'clock_out_latitude' : 'clock_in_latitude';
  const lngKey = prefix === 'out' ? 'clock_out_longitude' : 'clock_in_longitude';
  const accKey = prefix === 'out' ? 'clock_out_accuracy_metres' : 'clock_in_accuracy_metres';
  const geoKey = prefix === 'out' ? 'clock_out_geo_flag' : 'clock_in_geo_flag';
  const label = prefix === 'out' ? 'Clock-out GPS' : 'Clock-in GPS';
  if (row[latKey]) {
    return (
      <p className={`text-xs flex items-center gap-1 text-green-700`}>
        <MapPin className="h-3 w-3 shrink-0" />
        {label}: {row[latKey].toFixed(4)}, {row[lngKey].toFixed(4)}
        {row[accKey] ? ` (±${Math.round(row[accKey])}m)` : ''}
      </p>
    );
  }
  if (row[geoKey] === 'location_denied') {
    return (
      <p className="text-xs flex items-center gap-1 text-amber-700 font-medium">
        <MapPin className="h-3 w-3 shrink-0" />
        {label}: permission denied
      </p>
    );
  }
  return (
    <p className="text-xs flex items-center gap-1 text-slate-400">
      <MapPin className="h-3 w-3 shrink-0" />
      {label}: not captured
    </p>
  );
}

function normalizeOptionalId(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (!text) return null;
  const lowered = text.toLowerCase();
  if (lowered === 'none' || lowered === 'null' || lowered === 'undefined') return null;
  return text;
}

function getInitialNewShift(defaultServiceUserId = '') {
  const normalizedServiceUserId = normalizeOptionalId(defaultServiceUserId) || '';
  return {
    shift_type: normalizedServiceUserId ? 'service_user_visit' : 'care_location',
    start_at: '',
    end_at: '',
    location_text: '',
    role_required: '',
    service_user_id: normalizedServiceUserId,
    care_location_id: '',
    organisation_id: '',
    notes: '',
  };
}

function getServiceUserDisplayName(serviceUser) {
  if (!serviceUser) return '';
  return serviceUser.full_name || serviceUser.service_user_code || serviceUser.id || '';
}

function getServiceUserLocationFallback(serviceUser) {
  if (!serviceUser) return '';
  const name = getServiceUserDisplayName(serviceUser);
  const addressBits = [serviceUser.address_line_1, serviceUser.city, serviceUser.postcode].filter(Boolean);
  if (!name) return addressBits.join(', ');
  if (addressBits.length === 0) return name;
  return `${name} - ${addressBits.join(', ')}`;
}

function getShiftLocationLabel(shift) {
  if (!shift) return '';
  return shift?.location_text || shift?.care_location?.name || shift?.service_user_name || 'Location pending';
}

function normalizeRoleText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ')
    .trim();
}

function getRoleFamily(value) {
  const text = normalizeRoleText(value);
  if (!text) return 'unknown';

  const normalisedLabel = resolveKnownCareRoleLabel(text);
  if (normalisedLabel === 'Nurse') return 'nurse';
  if (normalisedLabel === 'Healthcare Assistant') return 'care';
  return 'unknown';
}

function isEmployeeEligibleForShift(shift, employee) {
  const shiftFamily = getRoleFamily(shift?.role_required);
  if (shiftFamily === 'unknown') return true;

  const employeeRole = employee?.role || employee?.job_title || employee?.system_role;
  const workerFamily = getRoleFamily(employeeRole);
  if (workerFamily === 'unknown') return true;
  return shiftFamily === workerFamily;
}

// ─── Operational State ────────────────────────────────────────────────────────

const OP_STATE_META = {
  open:               { label: 'Open',               mode: 'upcoming',     badgeClass: 'bg-blue-50 text-blue-700 border-blue-200',       hint: 'Needs assignment',                                     nextAction: 'Assign a worker'             },
  awaiting_worker:    { label: 'Awaiting worker',    mode: 'upcoming',     badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',    hint: 'Waiting for worker to accept',                         nextAction: 'Wait — or follow up'         },
  confirmed:          { label: 'Confirmed',           mode: 'upcoming',     badgeClass: 'bg-green-50 text-green-700 border-green-200',    hint: 'Worker has accepted',                                  nextAction: 'Monitor'                     },
  in_progress:            { label: 'In progress',            mode: 'upcoming',     badgeClass: 'bg-purple-50 text-purple-700 border-purple-200',    hint: 'Worker has clocked in',                                   nextAction: 'Await clock-out'            },
  attendance_submitted:    { label: 'Attendance submitted',    mode: 'needs_action', badgeClass: 'bg-indigo-50 text-indigo-700 border-indigo-200',    hint: 'Worker submitted attendance — awaiting review',           nextAction: 'Review attendance'          },
  attendance_approved:     { label: 'Attendance approved',     mode: 'history',      badgeClass: 'bg-green-50 text-green-700 border-green-200',       hint: 'Attendance verified — timesheet ready',                   nextAction: null                          },
  attendance_rejected:     { label: 'Attendance rejected',     mode: 'needs_action', badgeClass: 'bg-red-50 text-red-700 border-red-200',             hint: 'Attendance rejected — worker to resubmit',               nextAction: 'Await worker resubmission'  },
  worker_rejected:    { label: 'Worker rejected',    mode: 'needs_action', badgeClass: 'bg-red-50 text-red-700 border-red-200',          hint: 'Reassign or cancel this shift',                        nextAction: 'Reassign or cancel'          },
  past_unfilled:      { label: 'Past unfilled',      mode: 'needs_action', badgeClass: 'bg-orange-50 text-orange-800 border-orange-300', hint: 'Shift passed — no worker was ever assigned',           nextAction: 'Cancel this shift'           },
  past_pending:       { label: 'Past pending',       mode: 'needs_action', badgeClass: 'bg-orange-50 text-orange-700 border-orange-200', hint: 'Worker was assigned but never responded',              nextAction: 'Review — complete or cancel' },
  needs_completion:   { label: 'Needs completion',   mode: 'needs_action', badgeClass: 'bg-orange-50 text-orange-800 border-orange-300', hint: 'Mark this shift as completed',                         nextAction: 'Complete shift'              },
  needs_cancellation: { label: 'Needs cancellation', mode: 'needs_action', badgeClass: 'bg-red-50 text-red-700 border-red-200',          hint: 'Worker rejected this shift — cancel it',               nextAction: 'Cancel shift'                },
  completed:          { label: 'Completed',           mode: 'history',      badgeClass: 'bg-green-50 text-green-700 border-green-200',    hint: null,                                                   nextAction: null                          },
  cancelled:          { label: 'Cancelled',           mode: 'history',      badgeClass: 'bg-gray-100 text-gray-700 border-gray-300',      hint: null,                                                   nextAction: null                          },
};

function getShiftOperationalState(shift) {
  const now  = new Date();
  const past = shift.end_at ? new Date(shift.end_at) < now : false;
  const wr   = shift.latest_assignment?.worker_response_status || null;
  if (shift.status === 'completed') return 'completed';
  if (shift.status === 'cancelled') return 'cancelled';
  if (shift.status === 'open') {
    // After a worker rejection, the backend resets shift.status → 'open'.
    // latest_assignment still records the rejection — do not label this 'past_unfilled'.
    if (wr === 'rejected') return past ? 'needs_cancellation' : 'worker_rejected';
    return past ? 'past_unfilled' : 'open';
  }
  if (shift.status === 'assigned') {
    if (wr === 'accepted') {
      const attStatus = shift.current_attendance?.status;
      if (attStatus === 'submitted') return 'attendance_submitted';
      if (attStatus === 'approved') return 'attendance_approved';
      if (attStatus === 'rejected') return 'attendance_rejected';
      if (attStatus === 'open') return 'in_progress';
      return past ? 'needs_completion' : 'confirmed';
    }
    if (!past) {
      if (wr === 'rejected') return 'worker_rejected'; // defensive edge case
      return 'awaiting_worker';
    } else {
      if (wr === 'rejected') return 'needs_cancellation'; // defensive edge case
      return 'past_pending';
    }
  }
  return 'open';
}

function getAttendanceRejectedOperationalCopy(shift) {
  const resolution = shift?.current_attendance?.rejection_resolution;
  if (resolution === 'admin_correction_required') {
    return {
      hint: 'Attendance rejected — admin correction required',
      nextAction: 'Attendance on hold — office follow-up',
    };
  }
  if (resolution === 'escalated') {
    return {
      hint: 'Attendance rejected — escalated / under investigation',
      nextAction: 'Attendance on hold — office follow-up',
    };
  }
  return {
    hint: 'Attendance rejected — worker to resubmit',
    nextAction: 'Await worker resubmission',
  };
}

function getShiftOpStateMeta(shift, opState) {
  const base = OP_STATE_META[opState] || {};
  if (opState !== 'attendance_rejected') return base;
  const rejected = getAttendanceRejectedOperationalCopy(shift);
  return { ...base, hint: rejected.hint, nextAction: rejected.nextAction };
}

const MODE_STATES = {
  needs_action: new Set(['past_unfilled', 'worker_rejected', 'past_pending', 'needs_completion', 'needs_cancellation', 'attendance_submitted', 'attendance_rejected']),
  upcoming:     new Set(['open', 'awaiting_worker', 'confirmed', 'in_progress']),
  history:      new Set(['completed', 'cancelled', 'attendance_approved']),
  all:          null,
};

// Lower number = higher urgency = appears first in Needs Action view
const NEEDS_ACTION_ORDER = { attendance_submitted: 1, attendance_rejected: 2, needs_completion: 3, needs_cancellation: 4, past_pending: 5, past_unfilled: 6, worker_rejected: 7 };

const MODE_EMPTY_TEXT = {
  needs_action: 'No urgent shift actions — everything is under control.',
  upcoming:     'No upcoming shifts scheduled.',
  history:      'No completed or cancelled shifts found.',
  all:          'No shifts found.',
};

// ─────────────────────────────────────────────────────────────────────────────

export default function ShiftsPage() {
  const { token, isAuditor } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const serviceUserIdFilter = searchParams.get('service_user_id') || '';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [careLocations, setCareLocations] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [serviceUsers, setServiceUsers] = useState([]);
  const [billingOrgEditId, setBillingOrgEditId] = useState('');
  const [savingBillingOrg, setSavingBillingOrg] = useState(false);
  const [shiftMode, setShiftMode] = useState('needs_action');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [detailShift, setDetailShift] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceOnHoldRecords, setAttendanceOnHoldRecords] = useState([]);
  const [approvedAttendanceRecords, setApprovedAttendanceRecords] = useState([]);
  const [approvedQueueView, setApprovedQueueView] = useState('active');
  const [attendanceShiftsById, setAttendanceShiftsById] = useState({});
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceSaving, setAttendanceSaving] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [attendanceReviewOpen, setAttendanceReviewOpen] = useState(false);
  const [attendanceReviewAction, setAttendanceReviewAction] = useState(null);
  const [attendanceReviewReason, setAttendanceReviewReason] = useState('');
  const [adminCorrectOpen, setAdminCorrectOpen] = useState(false);
  const [adminCorrectRow, setAdminCorrectRow] = useState(null);
  const [adminCorrectIn, setAdminCorrectIn] = useState('');
  const [adminCorrectOut, setAdminCorrectOut] = useState('');
  const [adminCorrectReason, setAdminCorrectReason] = useState('');
  const [adminCorrectEvidenceSource, setAdminCorrectEvidenceSource] = useState('manager_decision');
  const [shiftApplications, setShiftApplications] = useState([]);
  const [shiftApplicationsLoading, setShiftApplicationsLoading] = useState(false);
  const [confirmingApplicationKey, setConfirmingApplicationKey] = useState(null);

  const [newShift, setNewShift] = useState(() => getInitialNewShift(serviceUserIdFilter));

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const activeEmployees = useMemo(
    () => (employees || []).filter((employee) => employee?.status === 'active'),
    [employees]
  );

  const pendingTimesheetStats = useMemo(() => {
    const rows = (approvedAttendanceRecords || []).filter(
      (row) => !row.timesheet_id && !row.fully_processed,
    );
    const totalMins = rows.reduce((sum, row) => {
      const pin = getPayableClockIn(row);
      const pout = getPayableClockOut(row);
      if (!pin || !pout) return sum;
      const diff = (new Date(pout) - new Date(pin)) / 60000;
      return diff > 0 ? sum + diff : sum;
    }, 0);
    return {
      count: rows.length,
      hours: totalMins > 0 ? Math.round((totalMins / 60) * 100) / 100 : 0,
    };
  }, [approvedAttendanceRecords]);
  const employeeById = useMemo(() => {
    const map = {};
    for (const employee of employees || []) {
      if (employee?.id) {
        map[employee.id] = employee;
      }
    }
    return map;
  }, [employees]);
  const serviceUserById = useMemo(() => {
    const map = {};
    for (const serviceUser of serviceUsers || []) {
      if (serviceUser?.id) {
        map[serviceUser.id] = serviceUser;
      }
    }
    return map;
  }, [serviceUsers]);
  const organisationById = useMemo(() => {
    const map = {};
    for (const org of organisations || []) {
      if (org?.id) map[org.id] = org;
    }
    return map;
  }, [organisations]);
  const shiftLocationById = useMemo(() => {
    const map = {};
    for (const shift of shifts || []) {
      if (shift?.id) map[shift.id] = getShiftLocationLabel(shift);
    }
    return map;
  }, [shifts]);

  const shiftById = useMemo(() => {
    const map = { ...attendanceShiftsById };
    for (const shift of shifts || []) {
      if (shift?.id) {
        map[shift.id] = shift;
      }
    }
    return map;
  }, [shifts, attendanceShiftsById]);

  const modeCounts = useMemo(() => {
    const counts = { needs_action: 0, upcoming: 0, history: 0 };
    for (const shift of shifts || []) {
      const opState = getShiftOperationalState(shift);
      if (MODE_STATES.needs_action.has(opState))       counts.needs_action++;
      else if (MODE_STATES.upcoming.has(opState))      counts.upcoming++;
      else if (MODE_STATES.history.has(opState))       counts.history++;
    }
    return counts;
  }, [shifts]);

  const visibleShifts = useMemo(() => {
    const allowed = MODE_STATES[shiftMode];
    const filtered = allowed
      ? (shifts || []).filter((s) => allowed.has(getShiftOperationalState(s)))
      : [...(shifts || [])];

    if (shiftMode === 'needs_action') {
      return [...filtered].sort((a, b) => {
        const aOrd = NEEDS_ACTION_ORDER[getShiftOperationalState(a)] ?? 99;
        const bOrd = NEEDS_ACTION_ORDER[getShiftOperationalState(b)] ?? 99;
        if (aOrd !== bOrd) return aOrd - bOrd;
        return new Date(a.end_at || 0) - new Date(b.end_at || 0);
      });
    }
    if (shiftMode === 'upcoming') {
      return [...filtered].sort((a, b) => new Date(a.start_at || 0) - new Date(b.start_at || 0));
    }
    if (shiftMode === 'history') {
      return [...filtered].sort((a, b) => new Date(b.end_at || 0) - new Date(a.end_at || 0));
    }
    return [...filtered].sort((a, b) => new Date(b.start_at || 0) - new Date(a.start_at || 0));
  }, [shifts, shiftMode]);

  const eligibleActiveEmployees = useMemo(() => {
    if (!selectedShift) return activeEmployees;
    return activeEmployees.filter((employee) => isEmployeeEligibleForShift(selectedShift, employee));
  }, [activeEmployees, selectedShift]);

  const getComplianceReason = (employee) => {
    const blockers = employee?.canonical_readiness?.blockers;
    if (!Array.isArray(blockers) || blockers.length === 0) return null;
    return String(blockers[0] || '').trim() || null;
  };

  const isComplianceEligible = (employee) => {
    const blockers = employee?.canonical_readiness?.blockers;
    if (!Array.isArray(blockers)) return true;
    return blockers.length === 0;
  };

  const assignableEligibleEmployees = useMemo(
    () => eligibleActiveEmployees.filter((employee) => isComplianceEligible(employee)),
    [eligibleActiveEmployees]
  );

  const blockedEligibleEmployees = useMemo(
    () => eligibleActiveEmployees.filter((employee) => !isComplianceEligible(employee)),
    [eligibleActiveEmployees]
  );

  const hasEligibleActiveEmployees = assignableEligibleEmployees.length > 0;

  useEffect(() => {
    if (!assignEmployeeId) return;
    const stillEligible = assignableEligibleEmployees.some((employee) => employee.id === assignEmployeeId);
    if (!stillEligible) {
      setAssignEmployeeId('');
    }
  }, [assignableEligibleEmployees, assignEmployeeId]);

  const getEmployeeDisplay = (employeeId) => {
    if (!employeeId) return 'Unknown employee';
    const employee = employeeById[employeeId];
    if (!employee) return employeeId;
    const fullName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim();
    return fullName || employee.employee_code || employee.id;
  };

  const getShiftDisplay = (shiftId) => {
    if (!shiftId) return 'Unknown shift';
    const shift = shiftById[shiftId];
    if (!shift) return shiftId;
    const location = getShiftLocationLabel(shift);
    const role = shift.role_required || 'Role pending';
    return `${location} — ${role} (${formatDateTime(shift.start_at)} → ${formatDateTime(shift.end_at)})`;
  };

  const getShiftLocationForAttendance = (shiftId) => {
    const shift = shiftById[shiftId];
    return getShiftLocationLabel(shift) || shiftLocationById[shiftId] || '—';
  };

  const getShiftRoleForAttendance = (shiftId) => {
    const shift = shiftById[shiftId];
    return shift?.role_required || '—';
  };

  const getShiftScheduledStartForAttendance = (shiftId) => {
    const shift = shiftById[shiftId];
    return shift?.start_at ? formatDateTime(shift.start_at) : '—';
  };

  const getShiftScheduledEndForAttendance = (shiftId) => {
    const shift = shiftById[shiftId];
    return shift?.end_at ? formatDateTime(shift.end_at) : '—';
  };

  const getEmployeeCodeForAttendance = (employeeId) => {
    const employee = employeeById[employeeId];
    return employee?.employee_code || '—';
  };

  const getApprovedAttendanceExportRows = () => {
    return (approvedAttendanceRecords || []).map((row) => ({
      employeeName: getEmployeeDisplay(row.employee_id),
      employeeCode: getEmployeeCodeForAttendance(row.employee_id),
      shiftLocation: getShiftLocationForAttendance(row.shift_id),
      roleRequired: getShiftRoleForAttendance(row.shift_id),
      scheduledStart: getShiftScheduledStartForAttendance(row.shift_id),
      scheduledEnd: getShiftScheduledEndForAttendance(row.shift_id),
      originalClockIn: formatDateTime(row.clock_in_at),
      originalClockOut: formatDateTime(row.clock_out_at),
      payableClockIn: formatDateTime(getPayableClockIn(row)),
      payableClockOut: formatDateTime(getPayableClockOut(row)),
      approvedAt: formatDateTime(row.reviewed_at),
      approvedForTimesheet: row.approved_for_timesheet ? 'Yes' : 'No',
      approvalTier: formatApprovalTierLabel(row.approval_tier),
      warningFlags: formatWarningFlagsForDisplay(row.warning_flags?.length ? row.warning_flags : buildAttendanceWarningFlags(row)),
      reviewReason: row.review_decision_reason || row.review_note || '—',
      evidenceSource: formatEvidenceSourceLabel(row.correction_evidence_source),
    }));
  };

  const csvEscape = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const htmlEscape = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const handleExportApprovedAttendanceCsv = () => {
    const rows = getApprovedAttendanceExportRows();
    if (rows.length === 0) {
      toast.error('No approved attendance records to export');
      return;
    }
    const headersRow = [
      'Employee Name',
      'Employee Code',
      'Shift Location/Care Location',
      'Role Required',
      'Scheduled Start',
      'Scheduled End',
      'Original Clock In',
      'Original Clock Out',
      'Payable Clock In',
      'Payable Clock Out',
      'Approved At',
      'Approved For Timesheet',
      'Approval Tier',
      'Warning Flags',
      'Review Reason',
      'Evidence Source',
    ];
    const csvLines = [
      headersRow.map(csvEscape).join(','),
      ...rows.map((item) => [
        item.employeeName,
        item.employeeCode,
        item.shiftLocation,
        item.roleRequired,
        item.scheduledStart,
        item.scheduledEnd,
        item.originalClockIn,
        item.originalClockOut,
        item.payableClockIn,
        item.payableClockOut,
        item.approvedAt,
        item.approvedForTimesheet,
        item.approvalTier,
        item.warningFlags,
        item.reviewReason,
        item.evidenceSource,
      ].map(csvEscape).join(',')),
    ];
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `approved_attendance_timesheet_ready_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handlePrintApprovedAttendance = () => {
    const rows = getApprovedAttendanceExportRows();
    if (rows.length === 0) {
      toast.error('No approved attendance records to print');
      return;
    }
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Could not open print window');
      return;
    }
    const generatedAt = formatDateTime(new Date().toISOString());
    const logoSrc = `${window.location.origin}/osabea_logo.png`;
    const tableRows = rows.map((item) => `
      <tr>
        <td>${htmlEscape(item.employeeName)}</td>
        <td>${htmlEscape(item.employeeCode)}</td>
        <td>${htmlEscape(item.shiftLocation)}</td>
        <td>${htmlEscape(item.roleRequired)}</td>
        <td>${htmlEscape(item.scheduledStart)}</td>
        <td>${htmlEscape(item.scheduledEnd)}</td>
        <td>${htmlEscape(item.originalClockIn)}</td>
        <td>${htmlEscape(item.originalClockOut)}</td>
        <td>${htmlEscape(item.payableClockIn)}</td>
        <td>${htmlEscape(item.payableClockOut)}</td>
        <td>${htmlEscape(item.approvedAt)}</td>
        <td>${htmlEscape(item.approvedForTimesheet)}</td>
        <td>${htmlEscape(item.approvalTier)}</td>
        <td>${htmlEscape(item.warningFlags)}</td>
        <td>${htmlEscape(item.reviewReason)}</td>
        <td>${htmlEscape(item.evidenceSource)}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Approved Attendance / Timesheet Ready</title>
          <style>
            :root {
              --brand-primary: #0D6E6E;
              --brand-secondary: #1F2937;
              --brand-muted: #6B7280;
              --brand-border: #D1D5DB;
              --surface: #F9FAFB;
            }
            * { box-sizing: border-box; }
            body {
              margin: 0;
              padding: 18px;
              font-family: "Segoe UI", Arial, sans-serif;
              color: var(--brand-secondary);
              background: #ffffff;
            }
            .document {
              max-width: 1040px;
              margin: 0 auto;
            }
            .header {
              text-align: center;
              margin-bottom: 12px;
            }
            .logo {
              max-height: 56px;
              width: auto;
              display: block;
              margin: 0 auto 8px;
            }
            .company {
              margin: 0;
              font-size: 14px;
              font-weight: 600;
              color: var(--brand-secondary);
            }
            .title {
              margin: 4px 0 0;
              font-size: 22px;
              font-weight: 700;
              color: var(--brand-primary);
            }
            .meta {
              margin: 6px 0 0;
              font-size: 12px;
              color: var(--brand-muted);
            }
            .section-heading {
              margin: 14px 0 8px;
              font-size: 13px;
              font-weight: 700;
              color: var(--brand-primary);
              text-transform: uppercase;
              letter-spacing: 0.04em;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 11px;
              background: #fff;
            }
            th, td {
              border: 1px solid var(--brand-border);
              padding: 7px;
              text-align: left;
              vertical-align: top;
            }
            th {
              background: var(--brand-primary);
              color: #ffffff;
              font-weight: 600;
            }
            tr:nth-child(even) td {
              background: var(--surface);
            }
            .footer {
              margin-top: 14px;
              padding-top: 8px;
              border-top: 1px solid #E5E7EB;
              text-align: center;
              color: var(--brand-muted);
              font-size: 11px;
            }
            @media print {
              body { padding: 0; }
              .document { max-width: none; }
            }
          </style>
        </head>
        <body>
          <main class="document">
            <header class="header">
              <img class="logo" src="${htmlEscape(logoSrc)}" alt="Osabea Healthcare Solutions logo" />
              <p class="company">Osabea Healthcare Solutions</p>
              <h1 class="title">Approved Attendance / Timesheet Ready</h1>
              <p class="meta">Generated: ${htmlEscape(generatedAt)}</p>
            </header>

            <h2 class="section-heading">Approved Attendance Register</h2>
            <table>
              <thead>
                <tr>
                  <th>Employee Name</th>
                  <th>Employee Code</th>
                  <th>Shift Location / Care Location</th>
                  <th>Role Required</th>
                  <th>Scheduled Start</th>
                  <th>Scheduled End</th>
                  <th>Original Clock In</th>
                  <th>Original Clock Out</th>
                  <th>Payable Clock In</th>
                  <th>Payable Clock Out</th>
                  <th>Approved At</th>
                  <th>Approved For Timesheet</th>
                  <th>Approval Tier</th>
                  <th>Warning Flags</th>
                  <th>Review Reason</th>
                  <th>Evidence Source</th>
                </tr>
              </thead>
              <tbody>${tableRows}</tbody>
            </table>

            <footer class="footer">
              <div>Osabea Healthcare Solutions</div>
              <div>Page generated from Compliance Portal</div>
            </footer>
          </main>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const fetchShifts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (serviceUserIdFilter) params.service_user_id = serviceUserIdFilter;
      const res = await axios.get(`${API}/shifts`, { params, headers });
      setShifts(res.data?.shifts || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveEmployees = async () => {
    try {
      const res = await axios.get(`${API}/staff/employees`, {
        headers,
      });
      const rows = Array.isArray(res.data) ? res.data : (res.data?.employees || []);
      const employeeIds = rows.map((employee) => employee.id).filter(Boolean);
      const readinessRes = employeeIds.length > 0
        ? await axios.get(`${API}/employees/unified-progress-summary`, {
            params: { employee_ids: employeeIds.join(',') },
            headers,
          }).catch(() => ({ data: [] }))
        : { data: [] };
      const readinessByEmployeeId = new Map(
        (readinessRes.data || []).map((summary) => [summary.employee_id, summary])
      );
      setEmployees(rows.map((employee) => ({
        ...employee,
        canonical_readiness: readinessByEmployeeId.get(employee.id) || null,
      })));
    } catch (error) {
      toast.error('Failed to load active employees for assignment');
    }
  };

  const fetchActiveCareLocations = async () => {
    try {
      const res = await axios.get(`${API}/care-locations`, {
        headers,
        params: { include_inactive: false },
      });
      const rows = Array.isArray(res.data)
        ? res.data
        : (res.data?.care_locations || []);
      setCareLocations(rows.filter((loc) => loc?.is_active !== false));
    } catch (error) {
      toast.error('Failed to load care locations');
    }
  };

  const fetchOrganisations = async () => {
    try {
      const res = await axios.get(`${API}/organisations`, {
        headers,
        params: { include_inactive: false },
      });
      setOrganisations((res.data?.organisations || []).filter((o) => o.is_active !== false));
    } catch {
      setOrganisations([]);
    }
  };

  const fetchServiceUsers = async () => {
    try {
      const res = await axios.get(`${API}/service-users`, { headers });
      const rows = Array.isArray(res.data) ? res.data : (res.data?.service_users || []);
      setServiceUsers(rows);
    } catch (error) {
      toast.error('Failed to load service users');
    }
  };

  const fetchShiftApplications = async () => {
    try {
      setShiftApplicationsLoading(true);
      const res = await axios.get(`${API}/shift-applications`, {
        headers,
        params: { status: 'pending' },
      });
      setShiftApplications(res.data?.applications || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load shift applications');
      setShiftApplications([]);
    } finally {
      setShiftApplicationsLoading(false);
    }
  };

  const applicationGroups = useMemo(() => {
    const byShiftId = new Map();
    for (const row of shiftApplications) {
      const shiftId = row?.application?.shift_id || row?.shift?.id;
      if (!shiftId) continue;
      if (!byShiftId.has(shiftId)) {
        byShiftId.set(shiftId, { shift: row.shift, applicants: [] });
      }
      byShiftId.get(shiftId).applicants.push(row);
    }
    return Array.from(byShiftId.values())
      .filter((group) => group.shift?.status === 'open' && !group.shift?.assigned_employee_id)
      .sort((a, b) => String(a.shift?.start_at || '').localeCompare(String(b.shift?.start_at || '')));
  }, [shiftApplications]);

  const fetchAttendanceQueue = async () => {
    try {
      setAttendanceLoading(true);
      const [submittedRes, approvedRes, rejectedRes] = await Promise.all([
        axios.get(`${API}/shift-attendance`, {
          headers,
          params: { status: 'submitted' },
        }),
        axios.get(`${API}/shift-attendance`, {
          headers,
          params: {
            status: 'approved',
            financial_queue: approvedQueueView,
          },
        }),
        axios.get(`${API}/shift-attendance`, {
          headers,
          params: { status: 'rejected' },
        }),
      ]);
      const submittedRows = submittedRes.data?.attendance_records || [];
      const approvedRows = approvedRes.data?.attendance_records || [];
      const rejectedRows = (rejectedRes.data?.attendance_records || []).filter(isAttendanceOnHoldRecord);
      const combinedRows = [...submittedRows, ...approvedRows, ...rejectedRows];
      setAttendanceRecords(submittedRows);
      setAttendanceOnHoldRecords(rejectedRows);
      setApprovedAttendanceRecords(approvedRows);

      const knownShiftIds = new Set((shifts || []).map((shift) => shift?.id).filter(Boolean));
      const missingShiftIds = [...new Set(combinedRows.map((row) => row?.shift_id).filter(Boolean))]
        .filter((shiftId) => !knownShiftIds.has(shiftId));
      if (missingShiftIds.length > 0) {
        const shiftResponses = await Promise.all(
          missingShiftIds.map((shiftId) => axios.get(`${API}/shifts/${shiftId}`, { headers }))
        );
        const next = {};
        for (const response of shiftResponses) {
          const shift = response.data?.shift;
          if (shift?.id) next[shift.id] = shift;
        }
        if (Object.keys(next).length > 0) {
          setAttendanceShiftsById((prev) => ({ ...prev, ...next }));
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load attendance review queue');
    } finally {
      setAttendanceLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchShifts();
    fetchActiveEmployees();
    fetchActiveCareLocations();
    fetchOrganisations();
    fetchServiceUsers();
    if (!isAuditor()) {
      fetchAttendanceQueue();
      fetchShiftApplications();
    }
  }, [token, serviceUserIdFilter, approvedQueueView]);

  const openAttendanceReview = (row, action) => {
    setSelectedAttendance(row);
    setAttendanceReviewAction(action);
    setAttendanceReviewReason('');
    if (action === 'approve_clean') {
      handleApproveAttendance(row.id, 'clean');
      return;
    }
    setAttendanceReviewOpen(true);
  };

  const handleApproveAttendance = async (attendanceId, tier = 'clean', reviewReason = '') => {
    if (!attendanceId) return;
    const body = tier === 'warning'
      ? { approval_tier: 'warning', review_decision_reason: reviewReason.trim() }
      : { approval_tier: 'clean' };
    try {
      setAttendanceSaving(true);
      await axios.post(
        `${API}/shift-attendance/${attendanceId}/approve`,
        body,
        { headers }
      );
      toast.success(tier === 'warning' ? 'Attendance approved with warning' : 'Attendance approved', {
        description: 'Next: generate timesheets for this pay period',
        action: {
          label: 'Timesheets',
          onClick: () => { window.location.href = '/portal/timesheets'; },
        },
      });
      setAttendanceReviewOpen(false);
      setSelectedAttendance(null);
      setAttendanceReviewReason('');
      fetchAttendanceQueue();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : detail?.message || 'Failed to approve attendance';
      toast.error(message);
    } finally {
      setAttendanceSaving(false);
    }
  };

  const handleSubmitAttendanceReview = async () => {
    if (!selectedAttendance?.id || !attendanceReviewAction) {
      toast.error('Attendance record not found');
      return;
    }
    const reason = (attendanceReviewReason || '').trim();
    if (reason.length < 3) {
      toast.error('Reason must be at least 3 characters');
      return;
    }
    try {
      setAttendanceSaving(true);
      if (attendanceReviewAction === 'approve_warning') {
        await handleApproveAttendance(selectedAttendance.id, 'warning', reason);
        return;
      }
      const resolutionMap = {
        reject_resubmit: 'worker_resubmit',
        reject_admin_correction: 'admin_correction_required',
        reject_escalate: 'escalated',
      };
      const rejection_resolution = resolutionMap[attendanceReviewAction];
      if (!rejection_resolution) {
        toast.error('Unknown review action');
        return;
      }
      await axios.post(
        `${API}/shift-attendance/${selectedAttendance.id}/reject`,
        { reason, rejection_resolution },
        { headers }
      );
      const successLabels = {
        worker_resubmit: 'Rejected — worker may resubmit on site',
        admin_correction_required: 'Marked for admin correction',
        escalated: 'Escalated for investigation',
      };
      toast.success(successLabels[rejection_resolution] || 'Attendance rejected');
      setAttendanceReviewOpen(false);
      setSelectedAttendance(null);
      setAttendanceReviewReason('');
      fetchAttendanceQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update attendance');
    } finally {
      setAttendanceSaving(false);
    }
  };

  const openAdminCorrectModal = (row) => {
    const shift = shiftById[row?.shift_id];
    setAdminCorrectRow(row);
    setAdminCorrectIn(isoToDatetimeLocalValue(row?.clock_in_at || shift?.start_at));
    setAdminCorrectOut(isoToDatetimeLocalValue(row?.clock_out_at || shift?.end_at));
    setAdminCorrectReason('');
    setAdminCorrectEvidenceSource('manager_decision');
    setAdminCorrectOpen(true);
  };

  const handleSubmitAdminCorrect = async () => {
    if (!adminCorrectRow?.id) {
      toast.error('Attendance record not found');
      return;
    }
    const correctedIn = toIso(adminCorrectIn);
    const correctedOut = toIso(adminCorrectOut);
    const reason = (adminCorrectReason || '').trim();
    if (!correctedIn || !correctedOut) {
      toast.error('Corrected clock-in and clock-out times are required');
      return;
    }
    if (reason.length < 3) {
      toast.error('Correction reason must be at least 3 characters');
      return;
    }
    try {
      setAttendanceSaving(true);
      await axios.post(
        `${API}/shift-attendance/${adminCorrectRow.id}/admin-correct`,
        {
          corrected_clock_in_at: correctedIn,
          corrected_clock_out_at: correctedOut,
          review_decision_reason: reason,
          evidence_source: adminCorrectEvidenceSource || null,
        },
        { headers },
      );
      toast.success('Attendance corrected and approved for timesheet');
      setAdminCorrectOpen(false);
      setAdminCorrectRow(null);
      fetchAttendanceQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to correct attendance');
    } finally {
      setAttendanceSaving(false);
    }
  };

  const attendanceReviewDialogMeta = {
    approve_warning: {
      title: 'Approve with warning',
      description: 'Evidence flags will be stored. A review reason is required.',
      submitLabel: 'Approve with warning',
      variant: 'default',
    },
    reject_resubmit: {
      title: 'Reject for worker resubmission',
      description: 'Worker can re-clock on site when they return. Reason is required.',
      submitLabel: 'Reject for resubmission',
      variant: 'destructive',
    },
    reject_admin_correction: {
      title: 'Admin correction required',
      description: 'Worker cannot re-clock. Correct times in Attendance On Hold when ready.',
      submitLabel: 'Require admin correction',
      variant: 'destructive',
    },
    reject_escalate: {
      title: 'Escalate / investigate',
      description: 'Attendance is held for investigation. Worker cannot re-clock.',
      submitLabel: 'Escalate',
      variant: 'destructive',
    },
  };

  const handleCreateShift = async (e) => {
    e.preventDefault();
    const startIso = toIso(newShift.start_at);
    const endIso = toIso(newShift.end_at);
    if (!startIso || !endIso) {
      toast.error('Start and end date/time are required');
      return;
    }
    const shiftType = newShift.shift_type || 'care_location';
    const serviceUserId = normalizeOptionalId(newShift.service_user_id);
    const careLocationId = normalizeOptionalId(newShift.care_location_id);
    const selectedServiceUser = serviceUserId ? serviceUserById[serviceUserId] : null;
    const selectedCareLocation = careLocationId
      ? careLocations.find((location) => location.id === careLocationId)
      : null;
    if (shiftType === 'care_location' && !careLocationId) {
      toast.error('Select a care location for this shift type');
      return;
    }
    if (shiftType === 'service_user_visit' && !serviceUserId) {
      toast.error('Select a service user for this shift type');
      return;
    }
    const autoLocationText = shiftType === 'care_location'
      ? (selectedCareLocation?.name || '')
      : shiftType === 'service_user_visit'
        ? getServiceUserLocationFallback(selectedServiceUser)
        : '';
    const locationText = (newShift.location_text || '').trim() || autoLocationText;
    if (!locationText) {
      toast.error('Location is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/shifts`,
        {
          start_at: startIso,
          end_at: endIso,
          location_text: locationText,
          role_required: newShift.role_required,
          service_user_id: serviceUserId,
          care_location_id: careLocationId,
          organisation_id: normalizeOptionalId(newShift.organisation_id) || null,
          notes: newShift.notes || null,
        },
        { headers }
      );
      toast.success('Shift created');
      setIsCreateOpen(false);
      setNewShift(getInitialNewShift(serviceUserIdFilter));
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shift');
    } finally {
      setSaving(false);
    }
  };

  const openShiftDetail = async (shiftId) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/shifts/${shiftId}`, { headers });
      setDetailShift(res.data || null);
      setBillingOrgEditId(res.data?.shift?.organisation_id || '');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load shift detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveBillingOrg = async () => {
    const shiftId = detailShift?.shift?.id;
    if (!shiftId) return;
    setSavingBillingOrg(true);
    try {
      await axios.patch(
        `${API}/shifts/${shiftId}`,
        { organisation_id: normalizeOptionalId(billingOrgEditId) || null },
        { headers },
      );
      toast.success('Billing client updated');
      await openShiftDetail(shiftId);
      await fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update billing client');
    } finally {
      setSavingBillingOrg(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedShift?.id || !assignEmployeeId) {
      toast.error('Select an employee to assign');
      return;
    }
    setAssigning(true);
    try {
      await axios.post(
        `${API}/shifts/${selectedShift.id}/assign`,
        { employee_id: assignEmployeeId, notes: assignNotes || null },
        { headers }
      );
      toast.success('Worker assigned to shift');
      setIsAssignOpen(false);
      setAssignEmployeeId('');
      setAssignNotes('');
      await fetchShifts();
      await fetchShiftApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign worker');
    } finally {
      setAssigning(false);
    }
  };

  const handleConfirmApplicant = async (shiftId, employeeId) => {
    if (!shiftId || !employeeId) return;
    const confirmKey = `${shiftId}:${employeeId}`;
    setConfirmingApplicationKey(confirmKey);
    setAssigning(true);
    try {
      await axios.post(
        `${API}/shifts/${shiftId}/assign`,
        { employee_id: employeeId, notes: 'Confirmed from shift application queue' },
        { headers },
      );
      toast.success('Worker confirmed and assigned to shift');
      await Promise.all([fetchShifts(), fetchShiftApplications()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm worker');
    } finally {
      setAssigning(false);
      setConfirmingApplicationKey(null);
    }
  };

  const handleUnassign = async (shiftId) => {
    try {
      await axios.post(
        `${API}/shifts/${shiftId}/unassign`,
        { reason: null },
        { headers }
      );
      toast.success('Worker unassigned');
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unassign worker');
    }
  };

  const handleComplete = async (shiftId) => {
    try {
      const response = await axios.post(`${API}/shifts/${shiftId}/complete`, {}, { headers });
      if (response.data?.warning) {
        toast.warning(response.data.warning);
      }
      toast.success('Shift marked as completed');
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete shift');
    }
  };

  const handleCancelShift = async () => {
    if (!selectedShift?.id) {
      toast.error('Shift not found');
      return;
    }
    const reason = (cancelReason || '').trim();
    if (reason.length < 3) {
      toast.error('Cancellation reason must be at least 3 characters');
      return;
    }
    setSaving(true);
    try {
      await axios.patch(
        `${API}/shifts/${selectedShift.id}`,
        { status: 'cancelled', cancel_reason: reason },
        { headers }
      );
      toast.success('Shift cancelled');
      setIsCancelOpen(false);
      setSelectedShift(null);
      setCancelReason('');
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel shift');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="shifts-page">
      {serviceUserIdFilter && (
        <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
          <span className="text-sm text-blue-700 font-medium">Filtered by service user</span>
          <Button
            variant="outline"
            size="sm"
            className="h-7 border-blue-200 text-blue-700 hover:bg-blue-100"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('service_user_id');
              setSearchParams(next, { replace: true });
            }}
          >
            Clear filter
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-text-primary">Shifts</h1>
          <p className="text-text-muted mt-1">Create and operate shifts with canonical assignment truth.</p>
        </div>
        {!isAuditor() && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Create Shift
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-xl">
              <DialogHeader>
                <DialogTitle>Create Shift</DialogTitle>
                <DialogDescription>Select the shift type, then confirm the linked location or service user.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateShift} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="start_at">Start</Label>
                    <Input
                      id="start_at"
                      type="datetime-local"
                      value={newShift.start_at}
                      onChange={(e) => setNewShift((prev) => ({ ...prev, start_at: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_at">End</Label>
                    <Input
                      id="end_at"
                      type="datetime-local"
                      value={newShift.end_at}
                      onChange={(e) => setNewShift((prev) => ({ ...prev, end_at: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shift_type">Shift Type</Label>
                  <Select
                    value={newShift.shift_type}
                    onValueChange={(value) => setNewShift((prev) => ({
                      ...prev,
                      shift_type: value,
                      service_user_id: value === 'care_location' || value === 'external_location' ? '' : prev.service_user_id,
                      care_location_id: value === 'service_user_visit' || value === 'external_location' ? '' : prev.care_location_id,
                    }))}
                  >
                    <SelectTrigger id="shift_type">
                      <SelectValue placeholder="Select shift type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="care_location">Care location shift</SelectItem>
                      <SelectItem value="service_user_visit">Service user visit</SelectItem>
                      <SelectItem value="external_location">External location</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role_required">Role Required</Label>
                  <Select
                    value={newShift.role_required || ''}
                    onValueChange={(value) => setNewShift((prev) => ({ ...prev, role_required: value }))}
                  >
                    <SelectTrigger id="role_required">
                      <SelectValue placeholder="Select role..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CARE_ROLE_OPTIONS.map((role) => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-text-muted">Standard roles only — this keeps charge rates, invoicing and compliance predictable.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billing_organisation_id">Billing client (for invoices)</Label>
                  <Select
                    value={newShift.organisation_id || 'none'}
                    onValueChange={(value) => setNewShift((prev) => ({
                      ...prev,
                      organisation_id: value === 'none' ? '' : value,
                    }))}
                  >
                    <SelectTrigger id="billing_organisation_id">
                      <SelectValue placeholder="Select commissioning organisation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No billing client</SelectItem>
                      {organisations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-text-muted">
                    Required before client invoicing. Care location is where work happens; this is who pays.
                  </p>
                </div>
                {newShift.shift_type === 'care_location' ? (
                  <div className="space-y-2">
                    <Label htmlFor="care_location_id">Care Location</Label>
                    <Select
                      value={newShift.care_location_id || 'none'}
                      onValueChange={(value) => {
                        const nextId = value === 'none' ? '' : value;
                        const selectedLocation = careLocations.find((loc) => loc.id === nextId);
                        setNewShift((prev) => ({
                          ...prev,
                          care_location_id: nextId,
                          service_user_id: '',
                          location_text: prev.location_text || selectedLocation?.name || '',
                        }));
                      }}
                    >
                      <SelectTrigger id="care_location_id">
                        <SelectValue placeholder="Select care location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select care location</SelectItem>
                        {careLocations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name} {loc.city ? `(${loc.city})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
                {newShift.shift_type === 'service_user_visit' ? (
                  <div className="space-y-2">
                    <Label htmlFor="service_user_id">Service User</Label>
                    <Select
                      value={newShift.service_user_id || 'none'}
                      onValueChange={(value) => {
                        const nextId = value === 'none' ? '' : value;
                        const selectedServiceUser = serviceUserById[nextId];
                        setNewShift((prev) => ({
                          ...prev,
                          service_user_id: nextId,
                          care_location_id: '',
                          location_text: prev.location_text || getServiceUserLocationFallback(selectedServiceUser),
                        }));
                      }}
                    >
                      <SelectTrigger id="service_user_id">
                        <SelectValue placeholder="Select service user" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select service user</SelectItem>
                        {serviceUsers.map((serviceUser) => (
                          <SelectItem key={serviceUser.id} value={serviceUser.id}>
                            {getServiceUserDisplayName(serviceUser) || serviceUser.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {newShift.service_user_id && serviceUserById[newShift.service_user_id] ? (
                      <p className="text-xs text-text-muted">
                        {getServiceUserLocationFallback(serviceUserById[newShift.service_user_id])}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                <div className="space-y-2">
                  <Label htmlFor="location_text">
                    {newShift.shift_type === 'external_location' ? 'Location' : 'Location Text Override / Fallback'}
                  </Label>
                  <Input
                    id="location_text"
                    value={newShift.location_text}
                    onChange={(e) => setNewShift((prev) => ({ ...prev, location_text: e.target.value }))}
                    placeholder={
                      newShift.shift_type === 'care_location'
                        ? 'Defaults to selected care location name'
                        : newShift.shift_type === 'service_user_visit'
                          ? 'Defaults to selected service user name/address'
                          : 'Enter external location'
                    }
                    required={newShift.shift_type === 'external_location'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shift_notes">Notes (optional)</Label>
                  <Textarea
                    id="shift_notes"
                    value={newShift.notes}
                    onChange={(e) => setNewShift((prev) => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Create
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card data-help-section="operations">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-primary" />
              Shift Operations
              <HelpSectionHint sectionId="operations" />
            </CardTitle>
            <p className="text-sm text-text-muted mt-0.5">Resolve shift coverage and completion issues before payroll.</p>
          </div>
          <div className="flex flex-wrap items-center gap-1 sm:flex-shrink-0">
            {[
              { key: 'needs_action', label: 'Needs action', count: modeCounts.needs_action },
              { key: 'upcoming',     label: 'Upcoming',     count: modeCounts.upcoming     },
              { key: 'history',      label: 'History',      count: modeCounts.history      },
              { key: 'all',          label: 'All',          count: (shifts || []).length   },
            ].map(({ key, label, count }) => (
              <button
                key={key}
                type="button"
                onClick={() => setShiftMode(key)}
                className={`text-xs px-3 py-1.5 rounded-md border whitespace-nowrap transition-colors ${
                  shiftMode === key
                    ? 'bg-primary text-primary-foreground border-primary font-medium'
                    : 'bg-white text-text-muted border-border hover:bg-muted/40'
                }`}
              >
                {label}{count > 0 ? ` (${count})` : ''}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : shifts.length === 0 ? (
            <p className="text-text-muted py-4">No shifts found.</p>
          ) : visibleShifts.length === 0 ? (
            <p className="text-text-muted py-4">{MODE_EMPTY_TEXT[shiftMode] || 'No shifts found.'}</p>
          ) : (
            <>
              {/* Desktop / tablet — compact table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-[900px] w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">State</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Next action</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Window</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Location / Role</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Service User</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Worker</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Response</th>
                      <th className="text-right py-2 px-3 font-medium text-text-muted whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleShifts.map((shift) => {
                      const assignedEmployee = shift.assigned_employee_id
                        ? employeeById[shift.assigned_employee_id]
                        : null;
                      // If rejected: shift reverts to open, assigned_employee_id → null.
                      // Show the last known worker for context.
                      const lastKnownEmployee = !assignedEmployee && shift.latest_assignment?.employee_id
                        ? employeeById[shift.latest_assignment.employee_id]
                        : null;
                      const assignedName = assignedEmployee
                        ? (
                            `${assignedEmployee.first_name || ''} ${assignedEmployee.last_name || ''}`.trim() ||
                            assignedEmployee.employee_code ||
                            shift.assigned_employee_id
                          )
                        : null;
                      const lastKnownName = lastKnownEmployee
                        ? (
                            `${lastKnownEmployee.first_name || ''} ${lastKnownEmployee.last_name || ''}`.trim() ||
                            lastKnownEmployee.employee_code ||
                            shift.latest_assignment.employee_id
                          )
                        : null;
                      const opState = getShiftOperationalState(shift);
                      const opMeta = getShiftOpStateMeta(shift, opState);
                      const isDimmed = opState === 'completed' || opState === 'cancelled';
                      const isExpired = shift.end_at ? new Date(shift.end_at) < new Date() : false;
                      const isCompleted = shift.status === 'completed';
                      const isCancelled = shift.status === 'cancelled';
                      const workerResponseStatus = shift.latest_assignment?.worker_response_status || null;

                      // Attendance-locked states block all destructive row actions.
                      // Admin must use Attendance Review Queue for submitted/open/approved/rejected.
                      const attStatus = shift.current_attendance?.status || null;
                      const attendanceLocked = attStatus === 'submitted' || attStatus === 'open' || attStatus === 'approved' || attStatus === 'rejected';

                      const canUnassign =
                        !attendanceLocked &&
                        !!shift.assigned_employee_id &&
                        !isCompleted &&
                        !isCancelled &&
                        !isExpired &&
                        workerResponseStatus !== 'rejected';

                      const canComplete =
                        !attendanceLocked &&
                        shift.status === 'assigned' &&
                        workerResponseStatus === 'accepted';

                      // Allow cancel for any non-terminal shift (open or assigned, past or future)
                      // but not while attendance is in a locked state.
                      const canCancel = !attendanceLocked && !isCompleted && !isCancelled;

                      const hasOverflowActions = canUnassign || canComplete || canCancel;

                      return (
                        <tr
                          key={shift.id}
                          className={`border-b last:border-0 hover:bg-muted/20 transition-colors${isDimmed ? ' opacity-75' : ''}`}
                        >
                          {/* State */}
                          <td className="py-2 px-3 align-middle whitespace-nowrap">
                            <Badge variant="outline" className={`text-xs w-fit ${opMeta?.badgeClass || ''}`}>
                              {opMeta?.label || shift.status}
                            </Badge>
                            {opMeta?.hint && (
                              <p className="text-xs text-text-muted mt-0.5 max-w-[130px] leading-tight">{opMeta.hint}</p>
                            )}
                          </td>
                          {/* Next action */}
                          <td className="py-2 px-3 align-middle whitespace-nowrap">
                            <span className="text-xs text-text-muted">
                              {opMeta?.nextAction || '—'}
                            </span>
                          </td>
                          {/* Window */}
                          <td className="py-2 px-3 align-middle whitespace-nowrap">
                            <p className="text-xs">{formatDateTime(shift.start_at)}</p>
                            <p className="text-xs text-text-muted">{formatDateTime(shift.end_at)}</p>
                          </td>
                          {/* Location / Role */}
                          <td className="py-2 px-3 align-middle max-w-[220px]">
                            <p className="font-medium text-text-primary truncate">{getShiftLocationLabel(shift)}</p>
                            {shift.role_required && (
                              <p className="text-xs text-text-muted truncate">{shift.role_required}</p>
                            )}
                          </td>
                          {/* Service User */}
                          <td className="py-2 px-3 align-middle">
                            <span className="text-xs text-text-muted">{shift.service_user_name || '—'}</span>
                          </td>
                          {/* Worker */}
                          <td className="py-2 px-3 align-middle">
                            {assignedName
                              ? <span className="text-xs text-text-primary">{assignedName}</span>
                              : lastKnownName
                                ? <span className="text-xs text-text-muted italic" title="Previously assigned; no longer active on this shift">{lastKnownName} <span className="not-italic">(prev.)</span></span>
                                : <span className="text-xs text-text-muted">—</span>
                            }
                          </td>
                          {/* Response */}
                          <td className="py-2 px-3 align-middle whitespace-nowrap">
                            {workerResponseStatus ? (
                              <span
                                className={`text-xs font-medium ${
                                  workerResponseStatus === 'accepted'
                                    ? 'text-green-700'
                                    : workerResponseStatus === 'rejected'
                                      ? 'text-red-700'
                                      : 'text-gray-600'
                                }`}
                              >
                                {workerResponseStatus}
                              </span>
                            ) : (
                              <span className="text-xs text-text-muted">—</span>
                            )}
                          </td>
                          {/* Actions */}
                          <td className="py-2 px-3 align-middle">
                            <div className="flex items-center justify-end gap-1">
                              {!isAuditor() && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => openShiftDetail(shift.id)}
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    View
                                  </Button>
                                  {shift.status !== 'completed' && shift.status !== 'cancelled' && !shift.assigned_employee_id && !isExpired && (
                                    <Button
                                      size="sm"
                                      className="h-7 text-xs"
                                      onClick={() => {
                                        setSelectedShift(shift);
                                        setIsAssignOpen(true);
                                      }}
                                    >
                                      <UserPlus className="h-3 w-3 mr-1" />
                                      Assign
                                    </Button>
                                  )}
                                  {hasOverflowActions && (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-7 w-7">
                                          <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        {canUnassign && (
                                          <DropdownMenuItem onClick={() => handleUnassign(shift.id)}>
                                            <UserX className="h-4 w-4 mr-2" />
                                            Unassign Worker
                                          </DropdownMenuItem>
                                        )}
                                        {canComplete && (
                                          <DropdownMenuItem onClick={() => handleComplete(shift.id)}>
                                            <CheckCircle2 className="h-4 w-4 mr-2" />
                                            Complete Shift
                                          </DropdownMenuItem>
                                        )}
                                        {canCancel && (
                                          <DropdownMenuItem
                                            onClick={() => {
                                              setSelectedShift(shift);
                                              setCancelReason('');
                                              setIsCancelOpen(true);
                                            }}
                                          >
                                            <UserX className="h-4 w-4 mr-2" />
                                            Cancel Shift
                                          </DropdownMenuItem>
                                        )}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile — stacked cards */}
              <div className="md:hidden space-y-3">
                {visibleShifts.map((shift) => {
                  const assignedEmployee = shift.assigned_employee_id
                    ? employeeById[shift.assigned_employee_id]
                    : null;
                  const assignedName = assignedEmployee
                    ? (
                        `${assignedEmployee.first_name || ''} ${assignedEmployee.last_name || ''}`.trim() ||
                        assignedEmployee.employee_code ||
                        shift.assigned_employee_id
                      )
                    : null;
                  const isDimmed = shift.status === 'completed' || shift.status === 'cancelled';
                  const isExpired = shift.end_at ? new Date(shift.end_at) < new Date() : false;
                  const isCompleted = shift.status === 'completed';
                  const isCancelled = shift.status === 'cancelled';
                  const workerResponseStatus = shift.latest_assignment?.worker_response_status || null;
                  const opState = getShiftOperationalState(shift);
                  const lastKnownEmployee = !assignedEmployee && shift.latest_assignment?.employee_id
                    ? employeeById[shift.latest_assignment.employee_id]
                    : null;
                  const lastKnownName = lastKnownEmployee
                    ? (
                        `${lastKnownEmployee.first_name || ''} ${lastKnownEmployee.last_name || ''}`.trim() ||
                        lastKnownEmployee.employee_code ||
                        shift.latest_assignment.employee_id
                      )
                    : null;

                  // Attendance-locked states block all destructive row actions.
                  // Admin must use Attendance Review Queue for submitted/open/approved/rejected.
                  const attStatus = shift.current_attendance?.status || null;
                  const attendanceLocked = attStatus === 'submitted' || attStatus === 'open' || attStatus === 'approved' || attStatus === 'rejected';

                  const canUnassign =
                    !attendanceLocked &&
                    !!shift.assigned_employee_id &&
                    !isCompleted &&
                    !isCancelled &&
                    !isExpired &&
                    workerResponseStatus !== 'rejected';

                  const canComplete =
                    !attendanceLocked &&
                    shift.status === 'assigned' &&
                    workerResponseStatus === 'accepted';

                  // Allow cancel for any non-terminal shift (open or assigned, past or future)
                  // but not while attendance is in a locked state.
                  const canCancel = !attendanceLocked && !isCompleted && !isCancelled;

                  const hasOverflowActions = canUnassign || canComplete || canCancel;

                  const cardBorderClass =
                    (opState === 'needs_completion' || opState === 'needs_cancellation' || opState === 'past_unfilled' || opState === 'past_pending') ? 'border-l-4 border-l-orange-400' :
                    opState === 'worker_rejected' ? 'border-l-4 border-l-red-400' :
                    opState === 'open' ? 'border-l-4 border-l-blue-400' :
                    opState === 'awaiting_worker' ? 'border-l-4 border-l-amber-400' :
                    (opState === 'confirmed' || opState === 'completed') ? 'border-l-4 border-l-green-400' :
                    'border-l-4 border-l-gray-300';
                  return (
                    <div
                      key={shift.id}
                      className={`flex flex-col rounded-lg border bg-white p-4 gap-3 ${cardBorderClass}${isDimmed ? ' opacity-75' : ''}`}
                    >
                      {/* Status row */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className={`text-xs ${OP_STATE_META[opState]?.badgeClass || ''}`}>
                            {OP_STATE_META[opState]?.label || shift.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          {shift.staffing_request_id && (
                            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                              Via request
                            </Badge>
                          )}
                          {!isAuditor() && hasOverflowActions && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-7 w-7">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canUnassign && (
                                  <DropdownMenuItem onClick={() => handleUnassign(shift.id)}>
                                    <UserX className="h-4 w-4 mr-2" />
                                    Unassign Worker
                                  </DropdownMenuItem>
                                )}
                                {canComplete && (
                                  <DropdownMenuItem onClick={() => handleComplete(shift.id)}>
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Complete Shift
                                  </DropdownMenuItem>
                                )}
                                {canCancel && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedShift(shift);
                                      setCancelReason('');
                                      setIsCancelOpen(true);
                                    }}
                                  >
                                    <UserX className="h-4 w-4 mr-2" />
                                    Cancel Shift
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>

                      {/* Shift details */}
                      <div className="flex-1 space-y-1">
                        <p className="font-semibold text-text-primary leading-tight">
                          {getShiftLocationLabel(shift)}
                        </p>
                        {shift.role_required && (
                          <p className="text-xs font-medium text-text-muted">{shift.role_required}</p>
                        )}
                        <p className="text-xs text-text-muted">
                          {formatDateTime(shift.start_at)} → {formatDateTime(shift.end_at)}
                        </p>
                        {shift.service_user_name && (
                          <p className="text-xs text-text-muted">
                            Service user: {shift.service_user_name}
                          </p>
                        )}
                      </div>

                      {/* Assignment block */}
                      {(assignedName || shift.latest_assignment?.worker_response_status) && (
                        <div className="rounded-md bg-gray-50 border px-3 py-2 space-y-0.5">
                          {assignedName && (
                            <p className="text-xs font-medium text-text-primary">{assignedName}</p>
                          )}
                          {shift.latest_assignment?.worker_response_status && (
                            <p className="text-xs text-text-muted">
                              {'Response: '}
                              <span
                                className={
                                  shift.latest_assignment.worker_response_status === 'accepted'
                                    ? 'text-green-700 font-medium'
                                    : shift.latest_assignment.worker_response_status === 'rejected'
                                      ? 'text-red-700 font-medium'
                                      : 'text-gray-600'
                                }
                              >
                                {shift.latest_assignment.worker_response_status}
                              </span>
                              {shift.latest_assignment.worker_response_note
                                ? ` — ${shift.latest_assignment.worker_response_note}`
                                : ''}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Primary actions */}
                      {!isAuditor() && (
                        <div className="flex items-center gap-2 pt-1 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => openShiftDetail(shift.id)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                          {shift.status !== 'completed' && shift.status !== 'cancelled' && !shift.assigned_employee_id && !isExpired && (
                            <Button
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => {
                                setSelectedShift(shift);
                                setIsAssignOpen(true);
                              }}
                            >
                              <UserPlus className="h-3 w-3 mr-1" />
                              Assign
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {!isAuditor() && (
        <Card data-testid="shift-applications-queue" data-help-section="applications">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Shift applications
              <HelpSectionHint sectionId="applications" />
            </CardTitle>
            <p className="text-sm text-text-muted">
              Workers who expressed interest in open shifts. Confirm one worker to assign the shift.
            </p>
          </CardHeader>
          <CardContent>
            {shiftApplicationsLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : applicationGroups.length === 0 ? (
              <p className="text-text-muted py-2">No pending shift applications.</p>
            ) : (
              <div className="space-y-4">
                {applicationGroups.map((group) => {
                  const shift = group.shift || {};
                  return (
                    <div key={shift.id} className="rounded-lg border p-3 sm:p-4 space-y-3">
                      <div className="space-y-1 text-sm">
                        <p className="font-medium text-text-primary">
                          {shift.role_required || 'Role pending'} — {formatDateTime(shift.start_at)} to {formatDateTime(shift.end_at)}
                        </p>
                        <p className="text-text-muted">{shift.location_text || 'Location pending'}</p>
                        {shift.care_location?.name ? (
                          <p className="text-text-muted">Care location: {shift.care_location.name}</p>
                        ) : null}
                      </div>
                      <div className="space-y-2">
                        {group.applicants.map((row) => {
                          const employee = row.employee || {};
                          const application = row.application || {};
                          const compliance = row.compliance_summary || {};
                          const confirmKey = `${shift.id}:${employee.id}`;
                          const employeeName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
                            || employee.employee_code
                            || employee.id
                            || 'Worker';
                          return (
                            <div
                              key={application.id || confirmKey}
                              className="flex flex-col gap-2 rounded-md bg-muted/30 p-3 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div className="space-y-1 text-sm min-w-0">
                                <p className="font-medium text-text-primary">{employeeName}</p>
                                <p className="text-text-muted">
                                  Role: {employee.role || employee.job_title || '—'}
                                </p>
                                <p className="text-text-muted">
                                  Applied: {formatDateTime(application.applied_at)}
                                </p>
                                {compliance.has_critical_blockers ? (
                                  <p className="text-xs text-red-700 font-medium">
                                    Compliance: {compliance.top_blocker || `${compliance.critical_blocker_count} critical blocker(s)`}
                                  </p>
                                ) : (
                                  <p className="text-xs text-green-700">Compliance: no critical blockers</p>
                                )}
                                {application.note ? (
                                  <p className="text-xs text-text-muted line-clamp-2">Note: {application.note}</p>
                                ) : null}
                              </div>
                              <Button
                                size="sm"
                                className="min-h-10 w-full sm:w-auto shrink-0"
                                onClick={() => handleConfirmApplicant(shift.id, employee.id)}
                                disabled={assigning || confirmingApplicationKey === confirmKey || compliance.has_critical_blockers}
                              >
                                {confirmingApplicationKey === confirmKey ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <UserPlus className="mr-2 h-4 w-4" />
                                )}
                                Confirm worker
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!isAuditor() && (
        <Card data-tour="shifts-attendance-review" data-help-section="attendance-review">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Attendance Review Queue
              <HelpSectionHint sectionId="attendance-review" />
            </CardTitle>
            <p className="text-sm text-text-muted">Workers who have clocked out — review before approving for timesheet.</p>
          </CardHeader>
          <CardContent>
            {attendanceLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : attendanceRecords.length === 0 ? (
              <p className="text-text-muted py-2">No submitted attendance records waiting for review.</p>
            ) : (
              <div className="space-y-3">
                {attendanceRecords.map((row) => {
                  const warningFlags = buildAttendanceWarningFlags(row);
                  const hasWarnings = warningFlags.length > 0;
                  return (
                  <div key={row.id} className={`rounded-lg border p-3 sm:p-4 ${hasWarnings ? 'border-amber-200 bg-amber-50/30' : ''}`}>
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="space-y-2 text-sm min-w-0 flex-1">
                        <p className="font-medium text-text-primary">Shift: {getShiftDisplay(row.shift_id)}</p>
                        <p className="text-text-muted">Employee: {getEmployeeDisplay(row.employee_id)}</p>
                        <p className="text-text-muted">Clock in: {formatDateTime(row.clock_in_at)}</p>
                        {row.clock_in_deviation_min != null && (
                          <p className={`text-xs flex items-center gap-1 ${row.clock_in_early_flag ? 'text-amber-700 font-medium' : 'text-text-muted'}`}>
                            {row.clock_in_early_flag && <AlertTriangle className="h-3 w-3 shrink-0" />}
                            Clock-in timing: {formatDeviationLabel(row.clock_in_deviation_min)}
                            {row.clock_in_early_flag ? ' — early flag' : ''}
                          </p>
                        )}
                        <p className="text-text-muted">Clock out: {formatDateTime(row.clock_out_at)}</p>
                        {row.clock_out_deviation_min != null && (
                          <p className={`text-xs flex items-center gap-1 ${Math.abs(row.clock_out_deviation_min) > ATTENDANCE_LATE_GRACE_MINUTES ? 'text-amber-700 font-medium' : 'text-text-muted'}`}>
                            {Math.abs(row.clock_out_deviation_min) > ATTENDANCE_LATE_GRACE_MINUTES && <AlertTriangle className="h-3 w-3 shrink-0" />}
                            Clock-out timing: {formatDeviationLabel(row.clock_out_deviation_min)}
                          </p>
                        )}
                        <div className="rounded-md border border-slate-100 bg-white/80 px-2 py-2 space-y-1">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Evidence</p>
                          {renderGpsEvidence('in', row)}
                          {renderSiteQrEvidence('in', row)}
                          {row.clock_out_at ? (
                            <>
                              {renderGpsEvidence('out', row)}
                              {renderSiteQrEvidence('out', row)}
                            </>
                          ) : null}
                        </div>
                        {hasWarnings ? (
                          <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5">
                            <p className="text-xs font-medium text-amber-800 flex items-center gap-1">
                              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                              Warning flags: {formatWarningFlagsForDisplay(warningFlags)}
                            </p>
                          </div>
                        ) : null}
                        {row.clock_in_note ? <p className="text-text-muted text-xs">Clock-in note: {row.clock_in_note}</p> : null}
                        {row.clock_out_note ? <p className="text-text-muted text-xs">Clock-out note: {row.clock_out_note}</p> : null}
                      </div>
                      <div className="flex shrink-0 flex-col items-stretch gap-2 sm:min-w-[11rem]">
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 w-fit">submitted</Badge>
                        <Button
                          size="sm"
                          onClick={() => openAttendanceReview(row, 'approve_clean')}
                          disabled={attendanceSaving}
                        >
                          Approve clean
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-300 text-amber-800 hover:bg-amber-50"
                          onClick={() => openAttendanceReview(row, 'approve_warning')}
                          disabled={attendanceSaving}
                        >
                          Approve with warning
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => openAttendanceReview(row, 'reject_resubmit')}
                          disabled={attendanceSaving}
                        >
                          Reject — resubmit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-200 text-red-700 hover:bg-red-50"
                          onClick={() => openAttendanceReview(row, 'reject_admin_correction')}
                          disabled={attendanceSaving}
                        >
                          Admin correction
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAttendanceReview(row, 'reject_escalate')}
                          disabled={attendanceSaving}
                        >
                          Escalate
                        </Button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!isAuditor() && (
        <Card data-testid="attendance-on-hold-card" data-help-section="on-hold">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Attendance On Hold
              <HelpSectionHint sectionId="on-hold" />
            </CardTitle>
            <p className="text-sm text-text-muted">
              Rejected attendance awaiting admin correction or investigation. Workers cannot resubmit.
              Use Correct &amp; approve for admin correction records.
            </p>
          </CardHeader>
          <CardContent>
            {attendanceLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : attendanceOnHoldRecords.length === 0 ? (
              <p className="text-text-muted py-2">No attendance records on hold for correction or investigation.</p>
            ) : (
              <div className="space-y-3">
                {attendanceOnHoldRecords.map((row) => {
                  const holdMeta = getAttendanceOnHoldMeta(row.rejection_resolution);
                  const warningFlags = buildAttendanceWarningFlags(row);
                  const hasWarnings = warningFlags.length > 0;
                  return (
                    <div
                      key={row.id}
                      className="rounded-lg border border-orange-200 bg-orange-50/20 p-3 sm:p-4"
                      data-testid={`attendance-on-hold-${row.id}`}
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="space-y-2 text-sm min-w-0 flex-1">
                          <p className="font-medium text-text-primary">{holdMeta.statusLine}</p>
                          <p className="text-text-muted">Shift: {getShiftDisplay(row.shift_id)}</p>
                          <p className="text-text-muted">Employee: {getEmployeeDisplay(row.employee_id)}</p>
                          <p className="text-text-muted">Clock in: {formatDateTime(row.clock_in_at)}</p>
                          <p className="text-text-muted">Clock out: {formatDateTime(row.clock_out_at)}</p>
                          {row.review_note ? (
                            <p className="text-xs text-red-800 bg-red-50 border border-red-100 rounded px-2 py-1.5">
                              <span className="font-medium">Reason: </span>
                              {row.review_note}
                            </p>
                          ) : null}
                          {row.reviewed_at ? (
                            <p className="text-xs text-text-muted">Held since {formatDateTime(row.reviewed_at)}</p>
                          ) : null}
                          <div className="rounded-md border border-slate-100 bg-white/80 px-2 py-2 space-y-1">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Evidence summary</p>
                            {renderGpsEvidence('in', row)}
                            {renderSiteQrEvidence('in', row)}
                            {row.clock_in_deviation_min != null && (
                              <p className="text-xs text-text-muted">
                                Clock-in timing: {formatDeviationLabel(row.clock_in_deviation_min)}
                                {row.clock_in_early_flag ? ' (early flag)' : ''}
                              </p>
                            )}
                            {row.clock_out_at ? (
                              <>
                                {renderGpsEvidence('out', row)}
                                {renderSiteQrEvidence('out', row)}
                                {row.clock_out_deviation_min != null && (
                                  <p className="text-xs text-text-muted">
                                    Clock-out timing: {formatDeviationLabel(row.clock_out_deviation_min)}
                                  </p>
                                )}
                              </>
                            ) : null}
                            {hasWarnings ? (
                              <p className="text-xs font-medium text-amber-800">
                                Flags: {formatWarningFlagsForDisplay(warningFlags)}
                              </p>
                            ) : (
                              <p className="text-xs text-slate-400">No warning flags recorded</p>
                            )}
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-2">
                          <Badge variant="outline" className={`w-fit ${holdMeta.badgeClass}`}>
                            {holdMeta.badgeLabel}
                          </Badge>
                          {row.rejection_resolution === 'admin_correction_required' ? (
                            <Button
                              size="sm"
                              onClick={() => openAdminCorrectModal(row)}
                              disabled={attendanceSaving}
                              data-testid={`admin-correct-${row.id}`}
                            >
                              Correct &amp; approve
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!isAuditor() && approvedQueueView === 'active' && (
        <Card data-tour="shifts-pending-timesheet" data-help-section="pending-timesheet">
          <CardContent className="pt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <Receipt className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary flex items-center gap-1.5">
                  <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">Pending Timesheet</Badge>
                  <HelpSectionHint sectionId="pending-timesheet" />
                </p>
                <p className="text-xs text-text-muted">
                  {pendingTimesheetStats.count} approved record{pendingTimesheetStats.count !== 1 ? 's' : ''}
                  {pendingTimesheetStats.hours > 0 ? ` · ${pendingTimesheetStats.hours.toFixed(2)}h payable` : ''}
                  {' '}awaiting timesheet generation — this is the predictable next step in the financial pipeline before pay and billing.
                </p>
              </div>
            </div>
            {pendingTimesheetStats.count > 0 ? (
              <Link to="/portal/timesheets">
                <Button variant="outline" size="sm">
                  <Receipt className="h-4 w-4 mr-1" />
                  Go to Timesheets
                </Button>
              </Link>
            ) : null}
          </CardContent>
        </Card>
      )}

      {!isAuditor() && (
        <Card
          data-tour="shifts-timesheet-ready"
          data-help-section={approvedQueueView === 'archive' ? 'archive' : 'timesheet-ready'}
        >
          <CardHeader>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {approvedQueueView === 'archive'
                    ? 'Processed / Archive'
                    : 'Approved Attendance / Timesheet Ready'}
                  <HelpSectionHint sectionId={approvedQueueView === 'archive' ? 'archive' : 'timesheet-ready'} />
                </CardTitle>
                <p className="text-sm text-text-muted mt-1">
                  {approvedQueueView === 'archive'
                    ? 'Financially Closed records (payroll processed + client invoiced). Read-only history. This is the end of the predictable pipeline.'
                    : 'Approved records awaiting timesheet, payroll, or invoicing. The badges show exact position in the financial pipeline. Export or print when ready.'}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant={approvedQueueView === 'archive' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setApprovedQueueView((v) => (v === 'active' ? 'archive' : 'active'))}
                >
                  {approvedQueueView === 'archive' ? 'Back to active queue' : 'View archive'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleExportApprovedAttendanceCsv}
                  disabled={approvedAttendanceRecords.length === 0 || approvedQueueView === 'archive'}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export CSV
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handlePrintApprovedAttendance}
                  disabled={approvedAttendanceRecords.length === 0 || approvedQueueView === 'archive'}
                >
                  <Printer className="h-4 w-4 mr-1" />
                  Print / PDF
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {attendanceLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : approvedAttendanceRecords.length === 0 ? (
              <p className="text-text-muted py-4">
                {approvedQueueView === 'archive'
                  ? 'No fully processed records in the archive yet. Records appear here after timesheet lock and client invoice send (or payroll-only when no billing client).'
                  : 'No active approved records waiting for timesheet or billing. Approved records will appear here until financially closed.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                {(() => {
                  const totalMins = approvedAttendanceRecords.reduce((sum, row) => {
                    const pin = getPayableClockIn(row);
                    const pout = getPayableClockOut(row);
                    if (!pin || !pout) return sum;
                    const diff = (new Date(pout) - new Date(pin)) / 60000;
                    return diff > 0 ? sum + diff : sum;
                  }, 0);
                  const totalHrsDisplay = totalMins > 0
                    ? `${Math.floor(totalMins / 60)}h ${Math.round(totalMins % 60)}m`
                    : null;
                  return (
                    <p className="text-xs text-text-muted mb-3">
                      {approvedAttendanceRecords.length} approved record{approvedAttendanceRecords.length !== 1 ? 's' : ''}
                      {totalHrsDisplay ? ` · ${totalHrsDisplay} total` : ''}
                    </p>
                  );
                })()}
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Employee</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Location / Role</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Scheduled</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Original</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Payable</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Timing</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Approved At</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Tier</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Warnings</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Review reason</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Evidence</th>
                      <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Status</th>
                      {approvedQueueView === 'active' ? (
                        <th className="text-left py-2 px-3 font-medium text-text-muted whitespace-nowrap">Actions</th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {approvedAttendanceRecords.map((row) => (
                      <tr key={row.id} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                        <td className="py-2 px-3 align-top">
                          <p className="font-medium text-text-primary">{getEmployeeDisplay(row.employee_id)}</p>
                          <p className="text-xs text-text-muted">{getEmployeeCodeForAttendance(row.employee_id)}</p>
                        </td>
                        <td className="py-2 px-3 align-top">
                          <p className="text-text-primary">{getShiftLocationForAttendance(row.shift_id)}</p>
                          <p className="text-xs text-text-muted">{getShiftRoleForAttendance(row.shift_id)}</p>
                        </td>
                        <td className="py-2 px-3 align-top whitespace-nowrap">
                          <p>{getShiftScheduledStartForAttendance(row.shift_id)}</p>
                          <p className="text-xs text-text-muted">{getShiftScheduledEndForAttendance(row.shift_id)}</p>
                        </td>
                        <td className="py-2 px-3 align-top whitespace-nowrap">
                          <p>{formatDateTime(row.clock_in_at)}</p>
                          <p className="text-xs text-text-muted">{formatDateTime(row.clock_out_at)}</p>
                        </td>
                        <td className="py-2 px-3 align-top whitespace-nowrap">
                          <p className="font-medium text-text-primary">{formatDateTime(getPayableClockIn(row))}</p>
                          <p className="text-xs text-text-muted">{formatDateTime(getPayableClockOut(row))}</p>
                        </td>
                        <td className="py-2 px-3 align-top whitespace-nowrap">
                          {row.clock_in_deviation_min != null ? (
                            <p className={`text-xs flex items-center gap-1 ${row.clock_in_early_flag ? 'text-amber-700' : 'text-text-muted'}`}>
                              {row.clock_in_early_flag && <AlertTriangle className="h-3 w-3" />}
                              {formatDeviationLabel(row.clock_in_deviation_min)}
                            </p>
                          ) : <span className="text-xs text-slate-400">—</span>}
                          {!row.clock_in_latitude && (
                            <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                              <MapPin className="h-3 w-3" />
                              no GPS
                            </p>
                          )}
                        </td>
                        <td className="py-2 px-3 align-top whitespace-nowrap">{formatDateTime(row.reviewed_at)}</td>
                        <td className="py-2 px-3 align-top whitespace-nowrap">
                          <Badge
                            variant="outline"
                            className={
                              row.approval_tier === 'warning'
                                ? 'bg-amber-50 text-amber-800 border-amber-200'
                                : row.approval_tier === 'corrected'
                                  ? 'bg-indigo-50 text-indigo-800 border-indigo-200'
                                  : 'bg-green-50 text-green-700 border-green-200'
                            }
                          >
                            {formatApprovalTierLabel(row.approval_tier)}
                          </Badge>
                        </td>
                        <td className="py-2 px-3 align-top text-xs text-text-muted max-w-[10rem]">
                          {formatWarningFlagsForDisplay(
                            row.warning_flags?.length ? row.warning_flags : (row.approval_tier === 'warning' ? buildAttendanceWarningFlags(row) : [])
                          )}
                        </td>
                        <td className="py-2 px-3 align-top text-xs text-text-muted max-w-[12rem]">
                          {row.review_decision_reason || row.review_note || '—'}
                        </td>
                        <td className="py-2 px-3 align-top text-xs text-text-muted whitespace-nowrap">
                          {formatEvidenceSourceLabel(row.correction_evidence_source)}
                        </td>
                        <td className="py-2 px-3 align-top space-y-1">
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">approved</Badge>
                          {row.fully_processed ? (
                            <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200 block w-fit">
                              Processed
                            </Badge>
                          ) : null}
                          {row.payroll_processed_at && !row.fully_processed ? (
                            <Badge variant="outline" className="bg-violet-50 text-violet-800 border-violet-200 block w-fit">
                              Payroll done
                            </Badge>
                          ) : null}
                          {row.invoice_processed_at && !row.fully_processed ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 block w-fit">
                              Invoiced
                            </Badge>
                          ) : null}
                          {row.timesheet_id && !row.payroll_processed_at ? (
                            <Badge variant="outline" className="bg-violet-50 text-violet-800 border-violet-200 block w-fit">
                              On timesheet
                            </Badge>
                          ) : null}
                          {row.fully_processed ? (
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300 block w-fit">
                              Financially Closed
                            </Badge>
                          ) : null}
                          {row.fully_processed ? (
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300 block w-fit">
                              Financially Closed
                            </Badge>
                          ) : null}
                        </td>
                        {approvedQueueView === 'active' ? (
                          <td className="py-2 px-3 align-top whitespace-nowrap">
                            {!row.timesheet_id && !row.fully_processed ? (
                              <Link to="/portal/timesheets">
                                <Button variant="outline" size="sm" className="h-8 text-xs">
                                  Generate timesheet
                                </Button>
                              </Link>
                            ) : (
                              <span className="text-xs text-text-muted">—</span>
                            )}
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={isAssignOpen} onOpenChange={setIsAssignOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Worker</DialogTitle>
            <DialogDescription>
              Select an active employee. Worker list is filtered by role requirements and non-compliant staff are disabled.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Active employee</Label>
              <Select value={assignEmployeeId} onValueChange={setAssignEmployeeId}>
                <SelectTrigger disabled={!hasEligibleActiveEmployees}>
                  <SelectValue placeholder={hasEligibleActiveEmployees ? 'Select employee' : 'No compliant active workers match this shift role.'} />
                </SelectTrigger>
                <SelectContent>
                  {assignableEligibleEmployees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} ({emp.employee_code || emp.id})
                    </SelectItem>
                  ))}
                  {blockedEligibleEmployees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id} disabled>
                      {emp.first_name} {emp.last_name} ({emp.employee_code || emp.id}) - Not compliant
                    </SelectItem>
                  ))}
                  {!hasEligibleActiveEmployees ? (
                    <div className="px-2 py-2 text-sm text-text-muted">No compliant active workers match this shift role.</div>
                  ) : null}
                </SelectContent>
              </Select>
              {blockedEligibleEmployees.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 space-y-1">
                  {blockedEligibleEmployees.slice(0, 3).map((emp) => (
                    <p key={emp.id}>
                      {emp.first_name} {emp.last_name}: {getComplianceReason(emp) || 'Non-compliant for assignment'}
                    </p>
                  ))}
                  {blockedEligibleEmployees.length > 3 && (
                    <p>+ {blockedEligibleEmployees.length - 3} more non-compliant worker(s)</p>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea value={assignNotes} onChange={(e) => setAssignNotes(e.target.value)} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAssignOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={assigning || !assignEmployeeId || !hasEligibleActiveEmployees}>
              {assigning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailShift || detailLoading} onOpenChange={(open) => !open && setDetailShift(null)}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Shift Detail</DialogTitle>
            <DialogDescription>Canonical shift record and assignment history.</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : detailShift?.shift ? (
            <div className="space-y-3 text-sm">
              {/* Operational state banner */}
              {(() => {
                const opS  = getShiftOperationalState({
                  ...detailShift.shift,
                  latest_assignment: detailShift.latest_assignment,
                  current_attendance: detailShift.current_attendance,
                });
                const meta = getShiftOpStateMeta(
                  {
                    ...detailShift.shift,
                    latest_assignment: detailShift.latest_assignment,
                    current_attendance: detailShift.current_attendance,
                  },
                  opS,
                );
                return (
                  <div className="rounded-md border p-3 bg-muted/30">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={meta?.badgeClass || ''}>
                        {meta?.label || detailShift.shift.status}
                      </Badge>
                      <span className="text-xs text-text-muted">
                        DB status: <span className="font-mono">{detailShift.shift.status}</span>
                      </span>
                    </div>
                    {meta?.nextAction && (
                      <p className="text-sm font-medium mt-1.5">Next action: {meta.nextAction}</p>
                    )}
                    {meta?.hint && (
                      <p className="text-xs text-text-muted mt-0.5">{meta.hint}</p>
                    )}
                  </div>
                );
              })()}
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                <p className="font-medium text-amber-900">Billing client (who pays the invoice)</p>
                {detailShift.shift.organisation_id ? (
                  <p className="text-sm text-text-primary">
                    {organisationById[detailShift.shift.organisation_id]?.name || detailShift.shift.organisation_id}
                  </p>
                ) : (
                  <p className="text-sm text-amber-800">
                    Not set — required for client invoicing. Select Test Organisation (or the commissioning client) below.
                  </p>
                )}
                {!isAuditor() && (
                  <div className="flex flex-wrap gap-2 items-end">
                    <Select
                      value={billingOrgEditId || 'none'}
                      onValueChange={(value) => setBillingOrgEditId(value === 'none' ? '' : value)}
                    >
                      <SelectTrigger className="max-w-xs h-9 bg-white">
                        <SelectValue placeholder="Select billing client" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No billing client</SelectItem>
                        {organisations.map((org) => (
                          <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSaveBillingOrg}
                      disabled={savingBillingOrg}
                    >
                      {savingBillingOrg ? 'Saving…' : 'Save billing client'}
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <p className="font-medium">Location</p>
                <p className="text-text-muted">{getShiftLocationLabel(detailShift.shift)}</p>
              </div>
              {detailShift.shift.care_location ? (
                <div>
                  <p className="font-medium">Linked Care Location</p>
                  <p className="text-text-muted">
                    {detailShift.shift.care_location.name}
                    {detailShift.shift.care_location.address_line_1 ? `, ${detailShift.shift.care_location.address_line_1}` : ''}
                    {detailShift.shift.care_location.city ? `, ${detailShift.shift.care_location.city}` : ''}
                    {detailShift.shift.care_location.postcode ? `, ${detailShift.shift.care_location.postcode}` : ''}
                  </p>
                </div>
              ) : null}
              {detailShift.shift.service_user_name ? (
                <div>
                  <p className="font-medium">Linked Service User</p>
                  <p className="text-text-muted">{detailShift.shift.service_user_name}</p>
                </div>
              ) : null}
              <div>
                <p className="font-medium">Role Required</p>
                <p className="text-text-muted">{detailShift.shift.role_required}</p>
              </div>
              <div>
                <p className="font-medium">Window</p>
                <p className="text-text-muted">
                  {formatDateTime(detailShift.shift.start_at)} → {formatDateTime(detailShift.shift.end_at)}
                </p>
              </div>
              {detailShift.shift.cancelled_reason ? (
                <div>
                  <p className="font-medium">Cancellation Reason</p>
                  <p className="text-text-muted">{detailShift.shift.cancelled_reason}</p>
                </div>
              ) : null}
              {/* Assignment story — truthful in all cases */}
              {(() => {
                const hasActive = !!detailShift.active_assignment;
                const latest    = detailShift.latest_assignment;
                const latestWr  = latest?.worker_response_status;

                if (hasActive) {
                  return (
                    <div>
                      <p className="font-medium">Assigned Worker</p>
                      <p className="text-text-muted">{getEmployeeDisplay(detailShift.active_assignment.employee_id)}</p>
                      <p className="text-xs text-text-muted">
                        {getEmployeeCodeForAttendance(detailShift.active_assignment.employee_id)}
                        {detailShift.active_assignment.assigned_at
                          ? ` · Assigned ${formatDateTime(detailShift.active_assignment.assigned_at)}`
                          : ''}
                      </p>
                    </div>
                  );
                }

                if (latest && latestWr === 'rejected') {
                  return (
                    <div>
                      <p className="font-medium">Assigned Worker</p>
                      <p className="text-xs font-medium text-amber-700 mb-1">
                        No active assignment — this worker rejected the shift. The shift reverted to open.
                      </p>
                      <p className="text-text-muted">
                        {getEmployeeDisplay(latest.employee_id)}
                        {' '}
                        <span className="text-xs text-text-muted">({getEmployeeCodeForAttendance(latest.employee_id)})</span>
                      </p>
                      {latest.assigned_at && (
                        <p className="text-xs text-text-muted">Assigned: {formatDateTime(latest.assigned_at)}</p>
                      )}
                      <p className="text-xs text-text-muted opacity-50 mt-0.5">Assignment ref: {latest.id}</p>
                    </div>
                  );
                }

                if (latest) {
                  return (
                    <div>
                      <p className="font-medium">Assigned Worker</p>
                      <p className="text-text-muted">No current assignment</p>
                      <p className="text-xs text-text-muted">
                        Previous: {getEmployeeDisplay(latest.employee_id)}
                        {' (‘'}{latest.status}{'’ assignment)'}
                      </p>
                    </div>
                  );
                }

                return (
                  <div>
                    <p className="font-medium">Assigned Worker</p>
                    <p className="text-text-muted">None — no worker has been assigned to this shift</p>
                  </div>
                );
              })()}
              {/* Worker response */}
              {detailShift.latest_assignment && (
                <div>
                  <p className="font-medium">Worker Response</p>
                  <p className="text-text-muted">
                    {(() => {
                      const wr = detailShift.latest_assignment.worker_response_status;
                      const colorClass =
                        wr === 'accepted' ? 'text-green-700 font-medium' :
                        wr === 'rejected' ? 'text-red-700 font-medium' : '';
                      return (
                        <>
                          <span className={colorClass}>
                            {wr === 'pending' ? 'Pending — no response yet' : (wr || 'No response yet')}
                          </span>
                          {detailShift.latest_assignment.worker_response_note
                            ? ` — ${detailShift.latest_assignment.worker_response_note}`
                            : ''}
                          {detailShift.latest_assignment.worker_responded_at
                            ? ` · ${formatDateTime(detailShift.latest_assignment.worker_responded_at)}`
                            : ''}
                        </>
                      );
                    })()}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-text-muted">No detail loaded.</p>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCancelOpen}
        onOpenChange={(open) => {
          setIsCancelOpen(open);
          if (!open) {
            setCancelReason('');
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Cancel Shift</DialogTitle>
            <DialogDescription>
              A cancellation reason is required and will be visible in worker-safe shift history.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel_reason">Cancellation reason</Label>
            <Textarea
              id="cancel_reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Explain why this shift was cancelled"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCancelOpen(false)}>
              Back
            </Button>
            <Button variant="destructive" onClick={handleCancelShift} disabled={saving || cancelReason.trim().length < 3}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Cancel shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={attendanceReviewOpen}
        onOpenChange={(open) => {
          setAttendanceReviewOpen(open);
          if (!open) {
            setSelectedAttendance(null);
            setAttendanceReviewReason('');
            setAttendanceReviewAction(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{attendanceReviewDialogMeta[attendanceReviewAction]?.title || 'Attendance review'}</DialogTitle>
            <DialogDescription>
              {attendanceReviewDialogMeta[attendanceReviewAction]?.description || 'Provide a reason for this decision.'}
            </DialogDescription>
          </DialogHeader>
          {selectedAttendance && attendanceReviewAction === 'approve_warning' ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Detected flags: {formatWarningFlagsForDisplay(buildAttendanceWarningFlags(selectedAttendance))}
            </div>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="attendance_review_reason">Reason / note</Label>
            <Textarea
              id="attendance_review_reason"
              value={attendanceReviewReason}
              onChange={(e) => setAttendanceReviewReason(e.target.value)}
              rows={3}
              placeholder="Required — recorded in attendance audit metadata"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttendanceReviewOpen(false)}>
              Back
            </Button>
            <Button
              variant={attendanceReviewDialogMeta[attendanceReviewAction]?.variant === 'destructive' ? 'destructive' : 'default'}
              onClick={handleSubmitAttendanceReview}
              disabled={attendanceSaving || attendanceReviewReason.trim().length < 3}
            >
              {attendanceSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {attendanceReviewDialogMeta[attendanceReviewAction]?.submitLabel || 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={adminCorrectOpen}
        onOpenChange={(open) => {
          setAdminCorrectOpen(open);
          if (!open) {
            setAdminCorrectRow(null);
            setAdminCorrectReason('');
          }
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Correct &amp; approve attendance</DialogTitle>
            <DialogDescription>
              Set payable times for timesheet. Original worker clock times are kept unchanged.
            </DialogDescription>
          </DialogHeader>
          {adminCorrectRow ? (
            <div className="space-y-4 text-sm">
              <div className="rounded-md border bg-muted/30 p-3 space-y-1">
                <p className="font-medium text-text-primary">{getEmployeeDisplay(adminCorrectRow.employee_id)}</p>
                <p className="text-text-muted">{getShiftDisplay(adminCorrectRow.shift_id)}</p>
                <p className="text-text-muted">
                  Scheduled: {getShiftScheduledStartForAttendance(adminCorrectRow.shift_id)} → {getShiftScheduledEndForAttendance(adminCorrectRow.shift_id)}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-text-muted mb-1">Original clock in</p>
                  <p className="font-medium">{formatDateTime(adminCorrectRow.clock_in_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted mb-1">Original clock out</p>
                  <p className="font-medium">{formatDateTime(adminCorrectRow.clock_out_at)}</p>
                </div>
              </div>
              <div className="rounded-md border border-slate-100 bg-slate-50 px-2 py-2 space-y-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Evidence summary</p>
                {renderGpsEvidence('in', adminCorrectRow)}
                {renderSiteQrEvidence('in', adminCorrectRow)}
                {renderGpsEvidence('out', adminCorrectRow)}
                {renderSiteQrEvidence('out', adminCorrectRow)}
                <p className="text-xs text-text-muted">
                  Flags: {formatWarningFlagsForDisplay(buildAttendanceWarningFlags(adminCorrectRow))}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="admin_correct_in">Corrected clock in</Label>
                  <Input
                    id="admin_correct_in"
                    type="datetime-local"
                    value={adminCorrectIn}
                    onChange={(e) => setAdminCorrectIn(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin_correct_out">Corrected clock out</Label>
                  <Input
                    id="admin_correct_out"
                    type="datetime-local"
                    value={adminCorrectOut}
                    onChange={(e) => setAdminCorrectOut(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin_correct_reason">Correction reason</Label>
                <Textarea
                  id="admin_correct_reason"
                  value={adminCorrectReason}
                  onChange={(e) => setAdminCorrectReason(e.target.value)}
                  rows={3}
                  placeholder="Why times were corrected (required)"
                />
              </div>
              <div className="space-y-2">
                <Label>Evidence source</Label>
                <Select value={adminCorrectEvidenceSource} onValueChange={setAdminCorrectEvidenceSource}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {CORRECTION_EVIDENCE_SOURCE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdminCorrectOpen(false)} disabled={attendanceSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAdminCorrect}
              disabled={
                attendanceSaving
                || adminCorrectReason.trim().length < 3
                || !adminCorrectIn
                || !adminCorrectOut
              }
            >
              {attendanceSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Correct &amp; approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

